// Collection grouping helpers for release list sections and the side index.
import { t } from '../../../shared/config/translations'
import type {
	ClientRelease,
	CollectionSortFilterOptions,
} from '../../../shared/types/collection'
import type { ReleaseListGroupInput } from '../../components/ReleaseList'

/** Shortens year labels for the compact side index display. */
export function getCollectionSideIndexLabel(label: string): string {
	return /^\d{4}$/.test(label) ? label.slice(-2) : label
}

/** Computes the release list group for the active collection sort mode. */
export function getCollectionReleaseGroup(
	release: ClientRelease,
	sort: CollectionSortFilterOptions,
	now = new Date(),
): ReleaseListGroupInput {
	if (sort.startsWith('added') || sort.startsWith('listened')) {
		const date = sort.startsWith('added')
			? new Date(release.date_added)
			: getLatestHistoryDate(release)
		if (!date || !Number.isFinite(date.getTime())) {
			return {
				key: 'no-date',
				label: sort.startsWith('added')
					? t('collection.groupNoAddedDate')
					: '#',
				sideIndexLabel: '#',
			}
		}
		return getDateGroup(date, now)
	}
	const key = sort.startsWith('artist')
		? getAlphabetGroupKey(release.data.artists?.[0]?.name)
		: sort.startsWith('title')
			? getAlphabetGroupKey(release.data.title)
			: getYearGroupKey(release, sort)

	return {
		key,
		label: key,
	}
}

/** Groups text values by first A-Z character, using # for other values. */
function getAlphabetGroupKey(value: unknown): string {
	if (typeof value !== 'string') return '#'
	const first = value.trim().charAt(0).toUpperCase()
	return /^[A-Z]$/.test(first) ? first : '#'
}

/** Selects the relevant year-like grouping key for date-based sort modes. */
function getYearGroupKey(
	release: ClientRelease,
	sort: CollectionSortFilterOptions,
): string {
	if (sort.startsWith('year')) {
		return normalizeYearGroupKey(release.data.year)
	}
	if (sort.startsWith('masterYear')) {
		return normalizeYearGroupKey(release.data.master_year)
	}
	if (sort.startsWith('rating')) {
		return release.rating ? String(release.rating) : '#'
	}
	return '#'
}

/** Converts a finite positive year number into a group label. */
function normalizeYearGroupKey(value: unknown): string {
	if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
		return '#'
	}
	return String(Math.trunc(value))
}

/** Uses the same latest dated listen as the collection's listened sort. */
function getLatestHistoryDate(release: ClientRelease): Date | null {
	const times = (release.history?.historyDates ?? [])
		.map((value) => new Date(value).getTime())
		.filter(Number.isFinite)
	return times.length ? new Date(Math.max(...times)) : null
}

/** Exclusive recency buckets, using local calendar days rather than 24-hour spans. */
function getDateGroup(date: Date, now: Date): ReleaseListGroupInput {
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const dayStart = (daysAgo: number) =>
		new Date(
			today.getFullYear(),
			today.getMonth(),
			today.getDate() - daysAgo,
		)
	const recentGroups = [
		{
			start: dayStart(-1),
			key: 'future',
			label: 'groupFuture',
			short: '→',
		},
		{ start: today, key: 'today', label: 'groupToday', short: 'TD' },
		{
			start: dayStart(1),
			key: 'yesterday',
			label: 'groupYesterday',
			short: 'YD',
		},
		{
			start: dayStart(6),
			key: 'last-7-days',
			label: 'groupLast7Days',
			short: '7d',
		},
		{
			start: dayStart(29),
			key: 'last-30-days',
			label: 'groupLast30Days',
			short: '30d',
		},
	]
	for (const group of recentGroups) {
		if (date >= group.start) {
			return {
				key: group.key,
				label: t(`collection.${group.label}`),
				sideIndexLabel: group.short,
			}
		}
	}

	// Clamp leap day to the last day of February in the previous year.
	const previousYear = today.getFullYear() - 1
	const anniversaryDay = Math.min(
		today.getDate(),
		new Date(previousYear, today.getMonth() + 1, 0).getDate(),
	)
	const yearAgo = new Date(previousYear, today.getMonth(), anniversaryDay)
	if (date >= yearAgo) {
		return {
			key: `month-${date.getFullYear()}-${date.getMonth() + 1}`,
			label: date.toLocaleDateString('en', {
				month: 'long',
				year: 'numeric',
			}),
			sideIndexLabel: date.toLocaleDateString('en', { month: 'short' }),
		}
	}
	const year = String(date.getFullYear())
	return { key: year, label: year }
}
